// ** Redux & Store & Actions
import { connect } from "react-redux";
import {
	setAgeCouple as setAgeCoupleAction,
	setAgeCouplePartner as setAgeCouplePartnerAction,
	setGenderCouple as setGenderCoupleAction,
	setGenderCouplePartner as setGenderCouplePartnerAction,
} from "store/modules/auth";

// ** Third Party Components
import { useTranslation } from "react-i18next";

// ** Custom Hooks
import useSelectorDay from "hooks/selectors/useSelectorDay";
import useSelectorMonth, {
	compute as monthCompute,
} from "hooks/selectors/useSelectorMonth";
import useSelectorYear from "hooks/selectors/useSelectorYear";

// ** Custom Components
import ErrorMessage from "containers/Landing/components/ErrorMessage";
import Block from "components/layout/Block";
import GenderButton from "../GenderButton";
import SelectorDay from "../SelectorDay";
import SelectorMonth from "../SelectorMonth";
import SelectorYear from "../SelectorYear";

// ** Styles Import
import { ButtonGroup } from "containers/Landing/components/ButtonGroup";

// #####################################################

const CoupleGenderContent = ({
	selectedDayPartner1,
	selectedMonthPartner1,
	selectedYearPartner1,
	selectedDayPartner2,
	selectedMonthPartner2,
	selectedYearPartner2,
	setAgeCouple,
	setAgeCouplePartner,
	selectedGenderCouple,
	selectedGenderCouplePartner,
	setGenderCouple,
	setGenderCouplePartner,
	dateErrorPartner1,
	dateErrorMessagePartner1,
	showGenderEmptyMessagePartner1,
	setShowGenderEmptyMessagePartner1,
	setShowGenderEmptyMessagePartner2,
	showAgeEmptyMessagePartner1,
	setShowAgeEmptyMessagePartner1,
	setShowAgeEmptyMessagePartner2,
}) => {
	const { t } = useTranslation("common");

	const selectorDay = useSelectorDay({ initial: selectedDayPartner1 });
	const selectorMonth = useSelectorMonth({
		initial: monthCompute.codeToValue(selectedMonthPartner1),
	});
	const selectorYear = useSelectorYear({ initial: selectedYearPartner1 });

	const selectorDayPartner = useSelectorDay({ initial: selectedDayPartner2 });
	const selectorMonthPartner = useSelectorMonth({
		initial: monthCompute.codeToValue(selectedMonthPartner2),
	});
	const selectorYearPartner = useSelectorYear({
		initial: selectedYearPartner2,
	});

	// **
	const handleSelectAge = ({ day, month, year }) => {
		setShowAgeEmptyMessagePartner1(false);
		setAgeCouple({ day, month, year });
	};

	// **
	const handleSelectAgePartner = ({ day, month, year }) => {
		setShowAgeEmptyMessagePartner2(false);
		setAgeCouplePartner({ day, month, year });
	};

	// **
	const handleSelectGender = (gender) => {
		setShowGenderEmptyMessagePartner1(false);
		setGenderCouple(gender);
	};

	// **
	const handleSelectGenderPartner = (genderPartner) => {
		setShowGenderEmptyMessagePartner2(false);
		setGenderCouplePartner(genderPartner);
	};

	// #####################################################

	return (
		<>
			<Block mb={1.2}>
				<ButtonGroup>
					<GenderButton
						label={t("sexOptions.woman")}
						onClick={() => handleSelectGender("woman")}
						active={selectedGenderCouple === "woman"}
					/>
					<GenderButton
						label={t("sexOptions.man")}
						onClick={() => handleSelectGender("man")}
						active={selectedGenderCouple === "man"}
					/>
					<GenderButton
						label={t("sexOptions.transsexual")}
						onClick={() => handleSelectGender("transsexual")}
						active={selectedGenderCouple === "transsexual"}
					/>
				</ButtonGroup>
			</Block>

			<Block flex column mb={dateErrorPartner1 ? 3 : 4}>
				<ButtonGroup>
					<SelectorDay
						selectorDay={selectorDay}
						onAccept={(day) => {
							handleSelectAge({ day });
						}}
					/>
					<SelectorMonth
						selectorMonth={selectorMonth}
						onAccept={(month) => {
							handleSelectAge({
								month: selectorMonth.compute.valueToApiParam(
									month
								),
							});
						}}
					/>
					<SelectorYear
						selectorYear={selectorYear}
						onAccept={(year) => {
							handleSelectAge({ year });
						}}
					/>
				</ButtonGroup>
			</Block>

			{/* Komunikaty Błędów */}
			{!dateErrorPartner1 && showGenderEmptyMessagePartner1 && (
				<ErrorMessage mb={3}>
					{t("landing:register.selectGenderEmpty")}
				</ErrorMessage>
			)}
			{!dateErrorPartner1 && showAgeEmptyMessagePartner1 && (
				<ErrorMessage mb={3}>
					{t("landing:register.dateEmpty")}
				</ErrorMessage>
			)}
			{dateErrorPartner1 && (
				<ErrorMessage mb={3}>{dateErrorMessagePartner1}</ErrorMessage>
			)}

			<Block mb={1.2}>
				<ButtonGroup>
					<GenderButton
						label={t("sexOptions.woman")}
						onClick={() => handleSelectGenderPartner("woman")}
						active={selectedGenderCouplePartner === "woman"}
					/>
					<GenderButton
						label={t("sexOptions.man")}
						onClick={() => handleSelectGenderPartner("man")}
						active={selectedGenderCouplePartner === "man"}
					/>
					<GenderButton
						label={t("sexOptions.transsexual")}
						onClick={() => handleSelectGenderPartner("transsexual")}
						active={selectedGenderCouplePartner === "transsexual"}
					/>
				</ButtonGroup>
			</Block>

			<Block flex column mb={2}>
				<ButtonGroup>
					<SelectorDay
						selectorDay={selectorDayPartner}
						onAccept={(day) => {
							handleSelectAgePartner({ day });
						}}
					/>
					<SelectorMonth
						selectorMonth={selectorMonthPartner}
						onAccept={(month) => {
							handleSelectAgePartner({
								month: selectorMonth.compute.valueToApiParam(
									month
								),
							});
						}}
					/>
					<SelectorYear
						selectorYear={selectorYearPartner}
						onAccept={(year) => {
							handleSelectAgePartner({ year });
						}}
					/>
				</ButtonGroup>
			</Block>
		</>
	);
};

// #####################################################

const mapStateToProps = ({
	auth: {
		register: {
			registerForm: {
				ageCoupleCase: {
					partner1: {
						day: selectedDayPartner1,
						month: selectedMonthPartner1,
						year: selectedYearPartner1,
					},
					partner2: {
						day: selectedDayPartner2,
						month: selectedMonthPartner2,
						year: selectedYearPartner2,
					},
				},
				genderCoupleCase: { partner1, partner2 },
			},
		},
	},
}) => ({
	selectedGenderCouple: partner1,
	selectedGenderCouplePartner: partner2,
	selectedDayPartner1,
	selectedMonthPartner1,
	selectedYearPartner1,
	selectedDayPartner2,
	selectedMonthPartner2,
	selectedYearPartner2,
});

const mapDispatchToProps = (dispatch) => ({
	setAgeCouple: (selectedAge) => dispatch(setAgeCoupleAction(selectedAge)),
	setAgeCouplePartner: (selectedAge) =>
		dispatch(setAgeCouplePartnerAction(selectedAge)),
	setGenderCouple: (selectedGender) =>
		dispatch(setGenderCoupleAction(selectedGender)),
	setGenderCouplePartner: (selectedGender) =>
		dispatch(setGenderCouplePartnerAction(selectedGender)),
});

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(CoupleGenderContent);

// #####################################################
